import {BackendApi} from "../api/api.ts";
import type {PayrollProvider} from "../generated/enums.ts";

export interface PayrollConnectUiParams {
    sessionToken: string
    publicProviderUrl: string
    apiProviderUrl: string
    providerConfigKey: string
}

export interface PayrollConnectUiOpener {
    open(params: PayrollConnectUiParams): Promise<string>
}

export interface PayrollConnectionStatus {
    connectionId: string | null
    provider: PayrollProvider | null
    name: string | null
    status: string | null
    isConnected: boolean
}

export interface AvailablePayrollProvider {
    provider: PayrollProvider
    name: string
}

export async function startPayrollConnection(
    api: BackendApi,
    opener: PayrollConnectUiOpener,
    provider: PayrollProvider,
): Promise<PayrollConnectionStatus> {
    const session = await api.Partner__payroll__connectSession({provider})

    const externalConnectionId = await opener.open({
        sessionToken: session.session_token,
        publicProviderUrl: session.public_provider_url,
        apiProviderUrl: session.api_provider_url,
        providerConfigKey: session.provider_config_key,
    })

    await api.Partner__payroll__connection__complete({
        connection_id: session.connection_id,
        external_connection_id: externalConnectionId,
    })

    return getPayrollConnectionStatus(api)
}

export async function reconnectPayrollProvider(
    api: BackendApi,
    opener: PayrollConnectUiOpener,
): Promise<PayrollConnectionStatus> {
    const session = await api.Partner__payroll__reconnectSession(null)

    const externalConnectionId = await opener.open({
        sessionToken: session.session_token,
        publicProviderUrl: session.public_provider_url,
        apiProviderUrl: session.api_provider_url,
        providerConfigKey: session.provider_config_key,
    })

    await api.Partner__payroll__connection__reconnectComplete({
        connection_id: session.connection_id,
        external_connection_id: externalConnectionId,
    })

    return getPayrollConnectionStatus(api)
}

export async function getPayrollConnectionStatus(api: BackendApi): Promise<PayrollConnectionStatus> {
    const response = await api.Partner__payroll__getPayrollConnection(null)

    return {
        connectionId: response.connection_id,
        provider: response.provider,
        name: response.name,
        status: response.status,
        isConnected: response.is_connected,
    }
}

export async function testPayrollConnection(api: BackendApi): Promise<boolean> {
    const response = await api.Partner__payroll__testConnection(null)
    return response.ok
}

export async function listAvailablePayrollProviders(api: BackendApi): Promise<AvailablePayrollProvider[]> {
    const response = await api.Partner__payroll__listAvailableProviders(null)

    return response.providers.map(provider => ({
        provider: provider.provider,
        name: provider.name,
    }))
}

export function canAddPayrollClients(provider: PayrollProvider): boolean {
    return provider === 'nmbrs'
}
